import React, {
  useState,
  useEffect,
  SetStateAction,
  Dispatch,
  useCallback,
  useRef,
} from 'react';
import { env } from '../../../../../../../../env';

export interface IMytaverseFetchResponse<DataType, CallLoadDataParams> {
  data: DataType;
  setData: Dispatch<SetStateAction<DataType>>;
  loading: boolean;
  callLoadData: (params: CallLoadDataParams) => void;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  loadNextPage: () => void;
  resetState: () => void;
  lastKey: any | null;
}

const PAGE_SIZE = env.REACT_APP_BOOKING_CALENDER_PAGE_SIZE || 10;
const INIT_PAGINATION = { page: 0, pageSize: +PAGE_SIZE };

const useMytaverseFetchPagination = (loadData: any) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState(null);

  const paginationRef = useRef(INIT_PAGINATION);
  const [lastKey, setLastKey] = useState(null);

  const axiosAbortControllerRef = useRef<any>();
  const loadDataPromiseRef = useRef<any>();

  const callLoadData = useCallback(
    async (params: any = {}, isLoadNext = false) => {
      if (loadDataPromiseRef.current && axiosAbortControllerRef.current) {
        axiosAbortControllerRef.current.abort();
      }

      setLoading(true);

      axiosAbortControllerRef.current = new AbortController();

      loadDataPromiseRef.current = loadData(params, {
        signal: axiosAbortControllerRef.current
          ? axiosAbortControllerRef.current.signal
          : null,
      })
        .then((response: any) => {
          const { data, lastKey = null } = response;

          loadDataPromiseRef.current = null;
          const newData = data !== undefined ? data : [];

          setData((prevState = []) => {
            return isLoadNext ? [...prevState, ...newData] : newData;
          });
          setLastKey(lastKey);

          setLoading(false);

          return;
        })
        .catch(() => {
          return;
        });

      return loadDataPromiseRef.current;
    },
    [loadData],
  );

  const resetState = useCallback(() => {
    paginationRef.current = INIT_PAGINATION;
    setLastKey(null);
    setData(null);
  }, []);

  const initLoadData = useCallback(
    async (params: any = {}) => {
      await callLoadData({
        ...params,
        limit:
          paginationRef.current.pageSize * (paginationRef.current.page + 1),
      });
    },
    [callLoadData],
  );

  const loadNextPage = useCallback(
    async (params: any = {}) => {
      paginationRef.current = {
        ...paginationRef.current,
        page: paginationRef.current.page + 1,
      };

      await callLoadData(
        {
          ...params,
          limit: paginationRef.current.pageSize,
          startKey: lastKey,
        },
        true,
      );
    },
    [callLoadData, lastKey],
  );

  useEffect(() => {
    return () => {
      if (!axiosAbortControllerRef || !axiosAbortControllerRef.current) {
        return;
      }
      axiosAbortControllerRef.current.abort();
    };
  }, [callLoadData]);

  return {
    data,
    setData,
    loading,
    error,
    callLoadData: initLoadData,
    setLoading,
    loadNextPage,
    resetState,
  };
};

export default useMytaverseFetchPagination;
